import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../../../i18/config";
import { getApprovals } from "../../../api";
import * as route from "../../../api/routes";
import { Approval } from "../types";
import ApprovalModal from "./ApprovalModal";
import { useDirectory } from "./useDirectory";

interface Props {
    /** The posts open in the editor (one per channel). */
    posts: { uuid?: string; status?: string }[];
    /** Called after a decision with the approval, whose `posts` carry the new statuses. */
    onDecided?: (approval: Approval) => void;
}

/**
 * In the post editor: says that a post waits for approval or was rejected
 * (with the reason), and lets an assignee decide right here.
 */
const ApprovalNotice = ({ posts, onDecided }: Props) => {
    const { t } = useTranslation(['workflow']);
    const { userLabel } = useDirectory();
    const [approval, setApproval] = useState<Approval | null>(null);
    const [open, setOpen] = useState(false);

    const held = posts.find((p) => p.uuid && (p.status === 'AA' || p.status === 'RJ'));
    const heldUuid = held?.uuid;
    const heldStatus = held?.status;

    const load = useCallback(() => {
        if (!heldUuid) {
            setApproval(null);

            return;
        }
        getApprovals({ post: heldUuid })
            .then((r: any) => setApproval((r.items || [])[0] || null))
            .catch(() => setApproval(null));
    }, [heldUuid]);

    useEffect(() => load(), [load, heldStatus]);

    if (!heldUuid) {
        return null;
    }

    const rejection = approval?.status === 'rejected' ? approval.decisions.filter((d) => d.decision === 'reject').pop() : undefined;

    return (
        <>
            <div className={'alert d-flex flex-wrap align-items-center gap-2 mb-3 ' + (heldStatus === 'RJ' ? 'alert-danger' : 'alert-warning')} role="alert">
                <i className={heldStatus === 'RJ' ? 'ri-close-circle-line fs-5' : 'ri-time-line fs-5'} />
                <span className="flex-grow-1">
                    {heldStatus === 'RJ'
                        ? <>{t('workflow:postRejected')}{rejection?.comment ? ': ' + rejection.comment : ''} <span className="text-muted small">({userLabel(rejection?.user)})</span></>
                        : t('workflow:postAwaitingApproval')}
                </span>
                {approval && <>
                    {approval.actions?.canDecide
                        ? <button type="button" className="btn btn-sm btn-success" onClick={() => setOpen(true)}>{t('workflow:decide')}</button>
                        : <button type="button" className="btn btn-sm btn-light" onClick={() => setOpen(true)}>{t('workflow:details')}</button>}
                    <Link to={route.AUTOMATION_APPROVALS} className="btn btn-sm btn-link">{t('workflow:allApprovals')}</Link>
                </>}
            </div>
            {open && <ApprovalModal approval={approval} userLabel={userLabel} onClose={() => setOpen(false)} onDecided={(decided) => { setOpen(false); setApproval(decided); onDecided?.(decided); }} />}
        </>
    );
};

export default ApprovalNotice;
